import React, { useContext, useMemo } from "react";
import Flex from "Trackalyze/Flex";
import { FilteredTracksContext } from "~/Context/FilteredTracksContext";
import { ImportContext } from "~/Context/ImportContext";
import { useTranslation } from "react-i18next";
import Typo from "Trackalyze/Typo";
import CheckBox from "Trackalyze/CheckBox";
import Icon from "Trackalyze/Icon";
import List, { ListItem } from "Trackalyze/List";

const FilterGpxTypesContent = () => {
  const { t } = useTranslation();

  const {
    filterGpxTypes,
    setFilterGpxTypes,
    filterActivities,
    filterWaypoints,
    filterFavorites,
    handleFilterTracks,
    searchQuery,
  } = useContext(FilteredTracksContext);

  const { tracksIndex } = useContext(ImportContext);

  const toggleGpxTypeFilter = (typeId) => {
    setFilterGpxTypes((prevFilter) => {
      if (prevFilter.includes(typeId)) {
        return prevFilter.filter((id) => id !== typeId);
      } else {
        return [...prevFilter, typeId];
      }
    });
  };

  const options = [
    { id: "duration", icon: "duration", label: "track.duration" },
    { id: "elevation", icon: "elevation", label: "track.elevation" },
    { id: "heartrate", icon: "heartrate", label: "track.heartrate" },
    { id: "temperature", icon: "temperature", label: "track.temperature" },
  ];

  // Calculate tracks filtered by other filters (activities and waypoints)
  const tracksFilteredByOthers = useMemo(() => {
    return handleFilterTracks(
      tracksIndex,
      filterActivities,
      filterWaypoints,
      filterFavorites || [],
      [], // Exclude current filter type to get potential matches
      [], // groups
      searchQuery,
    );
  }, [
    tracksIndex,
    filterActivities,
    filterWaypoints,
    filterFavorites,
    handleFilterTracks,
    searchQuery,
  ]);

  const availableOptions = useMemo(() => {
    return options
      .map((option) => {
        const isSelected = filterGpxTypes.includes(option.id);
        const count = tracksFilteredByOthers.filter((track) => {
          switch (option.id) {
            case "elevation":
              return (
                track["stats.elevation.ascent"] !== null ||
                track["stats.elevation.descent"] !== null
              );
            case "heartrate":
              return track["stats.heartrate.average"] !== null;
            case "duration":
              return track["stats.duration.total"] > 0;
            case "temperature":
              return track["stats.temperature.average"] !== null;
            default:
              return false;
          }
        }).length;

        return { ...option, isSelected, count };
      })
      .filter((opt) => opt.count > 0 || opt.isSelected);
  }, [options, filterGpxTypes, tracksFilteredByOthers]);

  if (availableOptions.length === 0) return null;

  return (
    <List segmented level="2">
      {availableOptions.map((option, idx) => {
        return (
          <ListItem key={idx}>
            <Flex
              itemscenter
              spacing="5"
              onClick={() => toggleGpxTypeFilter(option.id)}
            >
              <div
                data-gpx-type={option.id}
                data-gpx-variant="text"
                className="gpx_item"
              >
                <Icon symbol={option.icon} size="l" />
              </div>
              <Typo className="ta-flexitem">{t(option.label)}</Typo>
              <CheckBox
                name="gpxtype"
                value={option.id}
                checked={option.isSelected ? option.id : ""}
              />
            </Flex>
          </ListItem>
        );
      })}
    </List>
  );
};

export default React.memo(FilterGpxTypesContent);
